import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Man Aya';
const SITE_OG_IMAGE =
  'https://static.readdy.ai/image/4371883fa286ac0088227d507facd13a/ef79bfde2df0c1dd75275ce0accdb2bc.png';

interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  path: string;
}

const seoByPath: Record<string, SeoMeta> = {
  '/': {
    title: 'Man Aya — Build a Part-Time Income and Better Health',
    description:
      'I help ordinary people build part-time income and better health using systems I use myself. Sunday Surplus newsletter and a practical guide for beginners.',
    keywords: 'part-time income, better health, Sunday Surplus, income systems, beginners',
    path: '/',
  },
  '/sunday-surplus': {
    title: 'Sunday Surplus — Free Weekly Newsletter by Man Aya',
    description:
      'A free weekly email covering practical ways to build a part-time income and improve your health — sent every Sunday. Built for total beginners and amateurs.',
    keywords: 'Sunday Surplus, free newsletter, part-time income, health tips, weekly email',
    path: '/sunday-surplus',
  },
  '/the-system': {
    title: 'Prudent Income System: Partner Offer | Man Aya',
    description:
      'A complete part-time income system taught by someone actively running it — instant WhatsApp delivery, private community access, and weekly live Zoom calls.',
    keywords: 'Prudent Income System partner offer, part-time income, beginners',
    path: '/the-system',
  },
  '/book': {
    title: "The Millionaire's Guide — Easy Way to Make Money by Man Aya",
    description:
      'A practical PDF guide showing total beginners how to build a part-time income using proven systems — no fluff, just actionable steps.',
    keywords: "Millionaire's Guide, make money, part-time income, Man Aya, beginners guide",
    path: '/book',
  },
  '/about': {
    title: 'About Man Aya — The Story Behind Sunday Surplus',
    description:
      'The real story of Man Aya — from failed ventures and a sales career to running three active businesses and building a continent-wide movement.',
    keywords: 'about Man Aya, Man Aya story, Sunday Surplus founder, entrepreneur',
    path: '/about',
  },
};

const fallbackMeta: SeoMeta = {
  title: 'Man Aya — Build a Part-Time Income and Better Health',
  description:
    'I help ordinary people build part-time income and better health using systems I use myself.',
  keywords: 'part-time income, better health, Sunday Surplus, income systems',
  path: '/',
};

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function ensureLink(rel: string): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  ensureMeta(attr, key).setAttribute('content', content);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const meta = seoByPath[location.pathname] ?? fallbackMeta;
    const origin = (import.meta.env.VITE_SITE_URL || '').replace(/\/+$/, '');
    const canonicalUrl = `${origin}${meta.path === '/' ? '/' : meta.path}`;

    document.title = meta.title;

    setMeta('name', 'description', meta.description);
    setMeta('name', 'keywords', meta.keywords);

    ensureLink('canonical').setAttribute('href', canonicalUrl);

    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', SITE_OG_IMAGE);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    setMeta('name', 'twitter:image', SITE_OG_IMAGE);
  }, [location.pathname]);

  return null;
}